import {
	customerPortalUserProjectTable,
	linearCycleTable,
	linearIssueLabelTable,
	linearIssueTable,
	linearLabelTable,
	linearWorkflowStateTable,
} from "@mojo/db/schema";
import { createServerFn } from "@tanstack/react-start";
import { getRequestHeaders } from "@tanstack/react-start/server";
import { and, count, desc, eq, inArray, isNull, sql } from "drizzle-orm";
import { auth } from "@/lib/auth";
import { db } from "@/lib/db";

export type CycleWithStats = {
	id: string;
	name: string | null;
	number: number;
	description: string | null;
	startsAt: string;
	endsAt: string;
	progress: number;
	totalIssues: number;
	completedIssues: number;
	status: "current" | "upcoming" | "completed";
};

export type CycleScope = {
	id: string;
	title: string;
	type: "bug" | "improvement" | "feature";
	status: string; // workflow state type (backlog, unstarted, started, completed, canceled)
};

// Helper to get user's project IDs
async function getUserProjectIds(userId: string): Promise<string[]> {
	const userProjects = await db
		.select({ projectId: customerPortalUserProjectTable.projectId })
		.from(customerPortalUserProjectTable)
		.where(eq(customerPortalUserProjectTable.userId, userId));
	return userProjects.map((p) => p.projectId);
}

// Get all cycles for roadmap
export const getCycles = createServerFn({ method: "GET" }).handler(
	async (): Promise<CycleWithStats[]> => {
		const headers = getRequestHeaders();
		const session = await auth.api.getSession({ headers });

		if (!session?.user?.id) {
			return [];
		}

		const projectIds = await getUserProjectIds(session.user.id);

		if (projectIds.length === 0) {
			return [];
		}

		const today = new Date();
		today.setHours(0, 0, 0, 0);

		// Get cycles
		const cycles = await db
			.select()
			.from(linearCycleTable)
			.where(isNull(linearCycleTable.archivedAt))
			.orderBy(desc(linearCycleTable.startsAt));

		// Get completed workflow states
		const completedStates = await db
			.select({ id: linearWorkflowStateTable.id })
			.from(linearWorkflowStateTable)
			.where(eq(linearWorkflowStateTable.type, "completed"));
		const completedStateIds = completedStates.map((s) => s.id);

		// Helper to parse date string to Date object
		const parseDate = (dateStr: string): Date => {
			const d = new Date(dateStr);
			d.setHours(0, 0, 0, 0);
			return d;
		};

		// Get stats for each cycle
		const cyclesWithStats: CycleWithStats[] = await Promise.all(
			cycles.map(async (cycle) => {
				// Count total issues in cycle for user's projects
				const [{ total: totalIssues }] = await db
					.select({ total: count() })
					.from(linearIssueTable)
					.where(
						and(
							eq(linearIssueTable.cycleId, cycle.id),
							inArray(linearIssueTable.projectId, projectIds),
							isNull(linearIssueTable.archivedAt),
						),
					);

				// Count completed issues
				const [{ total: completedIssues }] = await db
					.select({ total: count() })
					.from(linearIssueTable)
					.where(
						and(
							eq(linearIssueTable.cycleId, cycle.id),
							inArray(linearIssueTable.projectId, projectIds),
							isNull(linearIssueTable.archivedAt),
							completedStateIds.length > 0
								? inArray(linearIssueTable.stateId, completedStateIds)
								: sql`false`,
						),
					);

				// Determine status based on date comparison
				const startsAt = parseDate(cycle.startsAt);
				const endsAt = parseDate(cycle.endsAt);

				let cycleStatus: "current" | "upcoming" | "completed";
				if (startsAt <= today && endsAt >= today) {
					cycleStatus = "current";
				} else if (startsAt > today) {
					cycleStatus = "upcoming";
				} else {
					cycleStatus = "completed";
				}

				return {
					id: cycle.id,
					name: cycle.name,
					number: cycle.number,
					description: cycle.description,
					startsAt: cycle.startsAt,
					endsAt: cycle.endsAt,
					progress: cycle.progress,
					totalIssues,
					completedIssues,
					status: cycleStatus,
				};
			}),
		);

		return cyclesWithStats;
	},
);

// Get current cycle
export const getCurrentCycle = createServerFn({ method: "GET" }).handler(
	async (): Promise<CycleWithStats | null> => {
		const headers = getRequestHeaders();
		const session = await auth.api.getSession({ headers });

		if (!session?.user?.id) {
			return null;
		}

		const projectIds = await getUserProjectIds(session.user.id);

		if (projectIds.length === 0) {
			return null;
		}

		const today = new Date();
		today.setHours(0, 0, 0, 0);
		const todayStr = today.toISOString().split("T")[0];

		// Get current cycle
		const [cycle] = await db
			.select()
			.from(linearCycleTable)
			.where(
				and(
					sql`${linearCycleTable.startsAt} <= ${todayStr}`,
					sql`${linearCycleTable.endsAt} >= ${todayStr}`,
					isNull(linearCycleTable.archivedAt),
				),
			)
			.orderBy(linearCycleTable.startsAt)
			.limit(1);

		if (!cycle) {
			return null;
		}

		// Get completed workflow states
		const completedStates = await db
			.select({ id: linearWorkflowStateTable.id })
			.from(linearWorkflowStateTable)
			.where(eq(linearWorkflowStateTable.type, "completed"));
		const completedStateIds = completedStates.map((s) => s.id);

		// Count total issues in cycle for user's projects
		const [{ total: totalIssues }] = await db
			.select({ total: count() })
			.from(linearIssueTable)
			.where(
				and(
					eq(linearIssueTable.cycleId, cycle.id),
					inArray(linearIssueTable.projectId, projectIds),
					isNull(linearIssueTable.archivedAt),
				),
			);

		// Count completed issues
		const [{ total: completedIssues }] = await db
			.select({ total: count() })
			.from(linearIssueTable)
			.where(
				and(
					eq(linearIssueTable.cycleId, cycle.id),
					inArray(linearIssueTable.projectId, projectIds),
					isNull(linearIssueTable.archivedAt),
					completedStateIds.length > 0
						? inArray(linearIssueTable.stateId, completedStateIds)
						: sql`false`,
				),
			);

		return {
			id: cycle.id,
			name: cycle.name,
			number: cycle.number,
			description: cycle.description,
			startsAt: cycle.startsAt,
			endsAt: cycle.endsAt,
			progress: cycle.progress,
			totalIssues,
			completedIssues,
			status: "current",
		};
	},
);

// Get upcoming cycles
export const getUpcomingCycles = createServerFn({ method: "GET" }).handler(
	async (): Promise<CycleWithStats[]> => {
		const headers = getRequestHeaders();
		const session = await auth.api.getSession({ headers });

		if (!session?.user?.id) {
			return [];
		}

		const projectIds = await getUserProjectIds(session.user.id);

		if (projectIds.length === 0) {
			return [];
		}

		const today = new Date();
		today.setHours(0, 0, 0, 0);
		const todayStr = today.toISOString().split("T")[0];

		// Get upcoming cycles
		const cycles = await db
			.select()
			.from(linearCycleTable)
			.where(
				and(
					sql`${linearCycleTable.startsAt} > ${todayStr}`,
					isNull(linearCycleTable.archivedAt),
				),
			)
			.orderBy(linearCycleTable.startsAt);

		// Get completed workflow states
		const completedStates = await db
			.select({ id: linearWorkflowStateTable.id })
			.from(linearWorkflowStateTable)
			.where(eq(linearWorkflowStateTable.type, "completed"));
		const completedStateIds = completedStates.map((s) => s.id);

		// Get stats for each cycle
		const cyclesWithStats: CycleWithStats[] = await Promise.all(
			cycles.map(async (cycle) => {
				// Count total issues in cycle for user's projects
				const [{ total: totalIssues }] = await db
					.select({ total: count() })
					.from(linearIssueTable)
					.where(
						and(
							eq(linearIssueTable.cycleId, cycle.id),
							inArray(linearIssueTable.projectId, projectIds),
							isNull(linearIssueTable.archivedAt),
						),
					);

				// Count completed issues
				const [{ total: completedIssues }] = await db
					.select({ total: count() })
					.from(linearIssueTable)
					.where(
						and(
							eq(linearIssueTable.cycleId, cycle.id),
							inArray(linearIssueTable.projectId, projectIds),
							isNull(linearIssueTable.archivedAt),
							completedStateIds.length > 0
								? inArray(linearIssueTable.stateId, completedStateIds)
								: sql`false`,
						),
					);

				return {
					id: cycle.id,
					name: cycle.name,
					number: cycle.number,
					description: cycle.description,
					startsAt: cycle.startsAt,
					endsAt: cycle.endsAt,
					progress: cycle.progress,
					totalIssues,
					completedIssues,
					status: "upcoming" as const,
				};
			}),
		);

		return cyclesWithStats;
	},
);

// Helper to derive issue type from labels
function deriveIssueType(
	labels: Array<{ name: string }>,
): "bug" | "improvement" | "feature" {
	for (const label of labels) {
		const name = label.name.toLowerCase();
		if (name.includes("bug")) return "bug";
		if (name.includes("improvement")) return "improvement";
		if (name.includes("feature")) return "feature";
	}
	return "feature"; // default
}

// Get scope (issues) for a specific cycle
export const getCycleScope = createServerFn({ method: "GET" })
	.inputValidator((d: string) => d)
	.handler(async ({ data: cycleId }): Promise<CycleScope[]> => {
		const headers = getRequestHeaders();
		const session = await auth.api.getSession({ headers });

		if (!session?.user?.id) {
			return [];
		}

		const projectIds = await getUserProjectIds(session.user.id);

		if (projectIds.length === 0) {
			return [];
		}

		// Get issues in this cycle for user's projects
		const issues = await db
			.select({
				id: linearIssueTable.id,
				title: linearIssueTable.title,
				stateType: linearWorkflowStateTable.type,
			})
			.from(linearIssueTable)
			.leftJoin(
				linearWorkflowStateTable,
				eq(linearIssueTable.stateId, linearWorkflowStateTable.id),
			)
			.where(
				and(
					eq(linearIssueTable.cycleId, cycleId),
					inArray(linearIssueTable.projectId, projectIds),
					isNull(linearIssueTable.archivedAt),
				),
			)
			.orderBy(linearIssueTable.linearUpdatedAt);

		// Get labels for all issues
		const issueIds = issues.map((i) => i.id);
		const issueLabels =
			issueIds.length > 0
				? await db
						.select({
							issueId: linearIssueLabelTable.issueId,
							labelName: linearLabelTable.name,
						})
						.from(linearIssueLabelTable)
						.innerJoin(
							linearLabelTable,
							eq(linearIssueLabelTable.labelId, linearLabelTable.id),
						)
						.where(inArray(linearIssueLabelTable.issueId, issueIds))
				: [];

		// Build result with derived types
		return issues.map((issue) => {
			const labels = issueLabels
				.filter((l) => l.issueId === issue.id)
				.map((l) => ({ name: l.labelName }));

			return {
				id: issue.id,
				title: issue.title,
				type: deriveIssueType(labels),
				status: issue.stateType ?? "backlog",
			};
		});
	});
